import type { CSSProperties, ReactNode } from 'react';
import type { ContainerPreset } from '@/constants/componentPresets';
import { ButtonPreset } from '@/constants/componentPresets';
import type { RadiusSize } from '@/theme/radius';
import { RadiusSize as RadiusSizeValue } from '@/theme/radius';
import type { ButtonPresetConfig } from './buttonPresetConfigs';
import type { ButtonContainerVisualProps } from './buttonContainerVisualProps';
import {
  renderButtonContent,
  renderButtonIcon,
  renderButtonText,
} from './buttonContent';
import { resolveClickAnimation } from './buttonInteraction';
import {
  resolveButtonPadding,
  resolveIconOnlySize,
  resolveIsIconOnly,
} from './buttonLayout';
import { type ButtonSizeProps, resolveButtonSizeProps } from './buttonSize';
import type { ButtonVisualState } from './buttonVisualState';
import {
  BUTTON_ICON_POSITION,
  type ButtonIconPosition,
  type CbuttonProps,
} from './buttonTypes';
import type { ButtonClickInvestigationHandlers } from './useButtonClickInvestigation';
import type { ButtonPressState } from './useButtonPressState';

const DEFAULT_ICON_SIZE = 16;
const DEFAULT_GAP = 8;

interface ButtonDefaultProps {
  iconSize: number;
  iconPosition: ButtonIconPosition;
  preset: ButtonPreset;
  radius: RadiusSize | number;
  gap: number;
  disabled: boolean;
  disableHoverEffects: boolean;
  isLoading: boolean;
}

export type ResolvedCbuttonProps = CbuttonProps ;
export type NormalizedCbuttonProps = Omit<
  ResolvedCbuttonProps,
  keyof ButtonDefaultProps
> &
  ButtonDefaultProps;

interface ResolveButtonContentModelInput {
  icon: NormalizedCbuttonProps['icon'];
  iconSize: NormalizedCbuttonProps['iconSize'];
  iconColor: NormalizedCbuttonProps['iconColor'];
  iconOpacity: NormalizedCbuttonProps['iconOpacity'];
  iconStyle: NormalizedCbuttonProps['iconStyle'];
  iconPosition: NormalizedCbuttonProps['iconPosition'];
  label: NormalizedCbuttonProps['label'];
  textSize: NormalizedCbuttonProps['textSize'];
  textWeight: ButtonVisualState['textWeight'];
  textColor: NormalizedCbuttonProps['textColor'];
  children: NormalizedCbuttonProps['children'];
  config: ButtonPresetConfig;
}

interface ResolveButtonLayoutModelInput {
  padding: NormalizedCbuttonProps['padding'];
  preset: NormalizedCbuttonProps['preset'];
  iconSize: NormalizedCbuttonProps['iconSize'];
  height: NormalizedCbuttonProps['height'];
  disableHoverEffects: NormalizedCbuttonProps['disableHoverEffects'];
  disableClickMotion: NormalizedCbuttonProps['disableClickMotion'];
  borderWidth: ButtonPresetConfig['borderWidth'];
  isInteractive: boolean;
  contentModel: ButtonContentModel;
}

interface ResolveButtonStyleModelInput {
  buttonStyle: CSSProperties;
  containerVisualProps: ButtonContainerVisualProps;
  resolvedContainerStyle: CSSProperties;
  glossy: number;
}

interface ResolveButtonRenderModelInput {
  contentModel: ButtonContentModel;
  layoutModel: ButtonLayoutModel;
  styleModel: ButtonStyleModel;
  ariaLabel: NormalizedCbuttonProps['ariaLabel'];
  label: NormalizedCbuttonProps['label'];
  href: NormalizedCbuttonProps['href'];
  isInteractive: boolean;
  pressState: ButtonPressState;
  clickInvestigationHandlers: ButtonClickInvestigationHandlers;
  containerPreset: NormalizedCbuttonProps['containerPreset'];
  radius: NormalizedCbuttonProps['radius'];
  gap: NormalizedCbuttonProps['gap'];
  preserveStyle: NormalizedCbuttonProps['preserveStyle'];
  isLoading: NormalizedCbuttonProps['isLoading'];
  className: NormalizedCbuttonProps['className'];
  beforeContent: NormalizedCbuttonProps['beforeContent'];
}

export interface ButtonContentModel {
  buttonContent: ReactNode;
  hasIcon: boolean;
  hasLabel: boolean;
  hasChildren: boolean;
}

export interface ButtonLayoutModel {
  buttonPadding: string | number;
  buttonSizeProps: ButtonSizeProps;
  clickAnimation: boolean;
}

export interface ButtonStyleModel {
  buttonStyle: CSSProperties;
  containerVisualProps: ButtonContainerVisualProps;
  resolvedContainerStyle: CSSProperties;
  glossy: number;
}

export interface ButtonRenderModel
  extends ButtonContentModel, ButtonLayoutModel, ButtonStyleModel {
  ariaLabel?: string;
  hasButtonSemantics: boolean;
  isInteractive: boolean;
  pressState: ButtonPressState;
  clickInvestigationHandlers: ButtonClickInvestigationHandlers;
  containerPreset?: ContainerPreset;
  radius: RadiusSize | number;
  gap: number;
  preserveStyle?: Record<string, number | string>;
  isLoading: boolean;
  className?: string;
  beforeContent?: ReactNode;
}

export function normalizeButtonProps({
  iconSize = DEFAULT_ICON_SIZE,
  iconPosition = BUTTON_ICON_POSITION.Left,
  preset = ButtonPreset.Primary,
  radius = RadiusSizeValue.Rounded,
  gap = DEFAULT_GAP,
  disabled = false,
  disableHoverEffects = false,
  isLoading = false,
  ...rest
}: ResolvedCbuttonProps): NormalizedCbuttonProps {
  return {
    ...rest,
    iconSize,
    iconPosition,
    preset,
    radius,
    gap,
    disabled,
    disableHoverEffects,
    isLoading,
  };
}

export function resolveButtonContentModel({
  icon,
  iconSize,
  iconColor,
  iconOpacity,
  iconStyle,
  iconPosition,
  label,
  textSize,
  textWeight,
  textColor,
  children,
  config,
}: ResolveButtonContentModelInput): ButtonContentModel {
  const iconElement = renderButtonIcon({
    icon,
    iconSize,
    iconColor,
    defaultIconColor: config.textColor,
    iconOpacity,
    iconStyle,
  });
  const textElement = renderButtonText({
    label,
    textSize,
    defaultTextSize: config.textSize,
    textWeight,
    textColor,
    defaultTextColor: config.textColor,
  });
  const hasIcon = Boolean(icon);
  const hasLabel = Boolean(label);
  const hasChildren = Boolean(children);

  return {
    buttonContent: renderButtonContent({
      children,
      icon,
      iconPosition,
      iconElement,
      textElement,
    }),
    hasIcon,
    hasLabel,
    hasChildren,
  };
}

export function resolveButtonLayoutModel({
  padding,
  preset,
  iconSize,
  height,
  disableHoverEffects,
  disableClickMotion,
  borderWidth,
  isInteractive,
  contentModel,
}: ResolveButtonLayoutModelInput): ButtonLayoutModel {
  const buttonPadding = resolveButtonPadding({
    padding,
    preset,
    hasIcon: contentModel.hasIcon,
    hasLabel: contentModel.hasLabel,
    hasChildren: contentModel.hasChildren,
  });
  const isIconOnly = resolveIsIconOnly(contentModel);
  const iconOnlySize = resolveIconOnlySize({
    isIconOnly,
    iconSize,
    padding: buttonPadding,
    borderWidth,
  });

  return {
    buttonPadding,
    buttonSizeProps: resolveButtonSizeProps({
      isIconOnly,
      iconOnlySize,
      height,
    }),
    clickAnimation: resolveClickAnimation({
      isInteractive,
      disableHoverEffects,
      disableClickMotion: disableClickMotion ?? false,
    }),
  };
}

export function resolveButtonStyleModel({
  buttonStyle,
  containerVisualProps,
  resolvedContainerStyle,
  glossy,
}: ResolveButtonStyleModelInput): ButtonStyleModel {
  return {
    buttonStyle,
    containerVisualProps,
    resolvedContainerStyle,
    glossy,
  };
}

export function resolveButtonRenderModel({
  contentModel,
  layoutModel,
  styleModel,
  ariaLabel,
  label,
  href,
  isInteractive,
  pressState,
  clickInvestigationHandlers,
  containerPreset,
  radius,
  gap,
  preserveStyle,
  isLoading,
  className,
  beforeContent,
}: ResolveButtonRenderModelInput): ButtonRenderModel {
  return {
    ...contentModel,
    ...layoutModel,
    ...styleModel,
    ariaLabel,
    // A link already has a role, a tab stop and Enter activation from the
    // platform. Adding `role="button"` on top would replace the link role in
    // the accessibility tree, and the synthetic Enter handler would fire the
    // click a second time on top of the browser's own navigation.
    hasButtonSemantics:
      isInteractive
      && href == null
      && Boolean(ariaLabel?.trim() || label?.trim()),
    isInteractive,
    pressState,
    clickInvestigationHandlers,
    containerPreset,
    radius,
    gap,
    preserveStyle,
    isLoading,
    className,
    beforeContent,
  };
}
